import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { openDrawer, selectCartCount } from './cartSlice';
import { useRegisterCartTarget } from '@/components/fx';
import { useStyleMode } from '@/features/restaurant/useStyleMode';
import { useSharedTableCart } from '@/features/tableSession/useSharedTableCart';
import MaskIcon from '@/components/ui/MaskIcon';

interface Props {
  restaurantUuid: string;
  /** 'bar' = desktop pill (default); 'cell' = compact icon + label column for the mobile action row;
      'compact' = icon-only, inherits currentColor so it stays legible on the dark waiter bar;
      'float' = mobile pill floated at the bottom while the header is scrolled away. */
  variant?: 'bar' | 'cell' | 'compact' | 'float';
}

export default function CartButton({ restaurantUuid, variant = 'bar' }: Props) {
  const { t } = useTranslation();
  const localCount = useAppSelector(selectCartCount(restaurantUuid));
  // SC-169: in shared-cart mode the badge reflects the server-side table cart
  // (sum of everyone's line quantities), not this phone's local cart.
  const shared = useSharedTableCart();
  const count = shared.isShared ? shared.count : localCount;
  const dispatch = useAppDispatch();
  const registerCartTarget = useRegisterCartTarget();
  const premium = useStyleMode() === 'premium';

  if (variant === 'compact') {
    // Icon-only trigger for the dark waiter bar: no background pill, no label,
    // and no text-color override so the masked icon inherits the bar's paper
    // text. Still registers the fly-to-cart target so the add animation lands.
    return (
      <button
        ref={registerCartTarget}
        type="button"
        data-fx-mute
        onClick={() => dispatch(openDrawer())}
        className="relative inline-flex items-center justify-center h-9 w-9 bg-transparent border-0 appearance-none"
        aria-label={`Ouvrir le panier (${count})`}
      >
        <MaskIcon src="/img/icon-takeaway-bag.svg" className="w-6 h-6" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-mint text-[var(--on-primary)] text-[10px] font-mono font-bold flex items-center justify-center rounded-full">
            {count}
          </span>
        )}
      </button>
    );
  }

  if (variant === 'cell') {
    return (
      <button
        ref={registerCartTarget}
        type="button"
        data-fx-mute
        onClick={() => dispatch(openDrawer())}
        className="relative flex flex-col items-center justify-end gap-1 w-[42px] pt-1 bg-transparent border-0 appearance-none text-ink"
        aria-label={`Ouvrir le panier (${count})`}
      >
        <span className="relative inline-flex">
          <MaskIcon src="/img/icon-takeaway-bag.svg" className="w-[25px] h-[25px]" />
          {count > 0 && (
            <span className="absolute -top-2 -right-2.5 min-w-[16px] h-4 px-1 bg-mint text-[var(--on-primary)] text-[10px] font-mono font-bold flex items-center justify-center rounded-full border-2 border-paper">
              {count}
            </span>
          )}
        </span>
        <span className="text-[8px] leading-none font-mono uppercase tracking-[0.03em] text-ink/70 whitespace-nowrap">{t('header.cart')}</span>
      </button>
    );
  }

  if (variant === 'float') {
    // An empty cart has nothing to lose: no pill over the menu until it holds
    // something. No aria-label: the visible, translated « Panier 2 » is the name.
    if (count === 0) return null;
    return (
      <span className="cart-float-in inline-flex">
        <button
          ref={registerCartTarget}
          type="button"
          data-fx-mute
          data-testid="floating-cart"
          onClick={() => dispatch(openDrawer())}
          className={`relative inline-flex items-center gap-2 h-11 px-4 bg-mint text-[var(--on-primary)] ${
            premium ? 'border uppercase tracking-[0.06em] text-[14px]' : 'border-2 text-base'
          } border-ink rounded-btn font-display shadow-stamp-sm appearance-none`}
        >
          <MaskIcon src="/img/icon-takeaway-bag.svg" className="w-5 h-5" />
          <span>{t('header.cart')}</span>
          <span className="min-w-[22px] h-[22px] px-1.5 bg-ink text-paper text-xs font-mono font-bold flex items-center justify-center rounded-full">
            {count}
          </span>
        </button>
      </span>
    );
  }

  return (
    <button
      ref={registerCartTarget}
      type="button"
      onClick={() => dispatch(openDrawer())}
      className={`relative flex items-center gap-1.5 sm:gap-2 bg-mint text-[var(--on-primary)] ${
        premium ? 'border uppercase tracking-[0.06em] text-[14px]' : 'border-2'
      } border-ink rounded-btn px-3 sm:px-4 py-2 font-display text-[13px] sm:text-base shadow-stamp-sm`}
      aria-label={`Ouvrir le panier (${count})`}
    >
      <MaskIcon src="/img/icon-user.svg" className="w-[17px] h-[17px]" />
      <span>{t('header.cart')}</span>
      {count > 0 && (
        <span className="absolute -top-2 -right-2 w-6 h-6 bg-ink text-paper text-xs font-mono flex items-center justify-center rounded-full">
          {count}
        </span>
      )}
    </button>
  );
}
